import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { istTodayIso } from '../utils/bookingWindow';
import { fmt12 } from './TimeField';
import { Button, Badge } from './ui';
import { Plus, Pencil, List, CalendarDays } from 'lucide-react';
import EventMonth from './EventMonth';
import EventFlags from './EventFlags';
import { agreedTotal } from '../utils/eventLines';
import { checkEventConflicts } from '../utils/eventConflicts';

// ─── Events — list view (Stage 2) and month grid (Stage 5) ───────────────────
// One row per `events` record. Events supersede private_charters: a charter is
// event_type 'private_charter'. Money here is the agreed total, derived from
// event_line_items (embedded in the one list query), never a collection.
//
// Dates: `events.event_date` / `end_date` are real `date` columns and arrive
// as YYYY-MM-DD. They are shown DD-MM-YYYY like everywhere else in the app.
// Times are real `time` columns and arrive as HH:MM:SS; the UI keeps HH:MM.

export const EVENT_TYPES = [
  { value: 'private_charter', label: 'Private charter' },
  { value: 'wedding',         label: 'Wedding' },
  { value: 'corporate',       label: 'Corporate' },
  { value: 'special_night',   label: 'Special night' },
  { value: 'maintenance',     label: 'Maintenance' },
  { value: 'other',           label: 'Other' },
];
export const typeLabel = (v) => (EVENT_TYPES.find(t => t.value === v) || {}).label || v || '';

// Badge tone per status. Gold is reserved for selection and primary actions, so
// no status uses it; confirmed is the one green on the screen.
export const STATUSES = [
  { value: 'tentative', label: 'Tentative', tone: 'amber' },
  { value: 'confirmed', label: 'Confirmed', tone: 'green' },
  { value: 'completed', label: 'Completed', tone: 'grey' },
  { value: 'cancelled', label: 'Cancelled', tone: 'red' },
];
const statusOf = (v) => STATUSES.find(s => s.value === v) || { label: v || '', tone: 'grey' };

// YYYY-MM-DD → DD-MM-YYYY
export const toDisplayDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${d}-${m}-${y}` : iso;
};
const weekdayOf = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short' }) : '');
// time column HH:MM:SS → HH:MM (what TimeField and fmt12 expect)
export const toHHMM = (t) => (t ? String(t).slice(0, 5) : '');

// PostgREST filter for "events that are on this day" (iso = YYYY-MM-DD):
// single-day events on the date, multi-day events whose span covers it.
// Cancelled events are excluded; they no longer occupy the vessel.
export const onDayFilter = (iso) =>
  `and(event_date.eq.${iso},end_date.is.null),and(event_date.lte.${iso},end_date.gte.${iso})`;
export const eventsOnDay = (iso) =>
  supabase.from('events').select('*').or(onDayFilter(iso)).neq('status', 'cancelled').order('created_at');

const fmtMoney = (n) => (n === null || n === undefined || n === '' ? '' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`);
const agreedOf = (ev) => agreedTotal(ev.event_line_items);
const paxText = (min, max) => {
  if (min == null && max == null) return '';
  if (min == null) return String(max);
  if (max == null || max === min) return String(min);
  return `${min} to ${max}`;
};
const timeText = (ev) => {
  const a = toHHMM(ev.start_time), b = toHHMM(ev.end_time);
  if (a && b) return `${fmt12(a)} to ${fmt12(b)}`;
  return a ? `from ${fmt12(a)}` : b ? `until ${fmt12(b)}` : '';
};
const lastDay = (ev) => ev.end_date || ev.event_date;

// Filters across the top. Upcoming is the default and the working list; past
// and cancelled stay one tap away. Status chips search every date.
const FILTERS = [
  { key: 'upcoming',  label: 'Upcoming' },
  { key: 'tentative', label: 'Tentative' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'past',      label: 'Past' },
];
const applyFilter = (rows, key, today) => {
  const asc  = (a, b) => a.event_date.localeCompare(b.event_date) || (a.start_time || '').localeCompare(b.start_time || '');
  const desc = (a, b) => -asc(a, b);
  switch (key) {
    case 'upcoming':
      return rows.filter(r => r.status !== 'cancelled' && r.status !== 'completed' && lastDay(r) >= today).sort(asc);
    case 'past':
      return rows.filter(r => r.status !== 'cancelled' && (r.status === 'completed' || lastDay(r) < today)).sort(desc);
    case 'completed':
    case 'cancelled':
      return rows.filter(r => r.status === key).sort(desc);
    default:
      return rows.filter(r => r.status === key).sort(asc);
  }
};
// The month grid carries the same chips, but a month is its own window: Upcoming
// drops the date bound (every open event in the month), Past keeps it.
const applyGridFilter = (rows, key, today) => {
  if (key === 'upcoming') return rows.filter(r => r.status !== 'cancelled' && r.status !== 'completed');
  return applyFilter(rows, key, today);
};
const filterLabelOf = (key) => (FILTERS.find(f => f.key === key) || {}).label || '';

// Wide screens only: the seven-column grid is never squeezed onto a phone.
const WIDE = '(min-width: 900px)';
const useWide = () => {
  const [wide, setWide] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(WIDE).matches : true));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(WIDE);
    const onChange = (e) => setWide(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return wide;
};

const EMPTY_COPY = {
  upcoming:  'Nothing on the calendar yet. Add an event and it appears here.',
  tentative: 'No tentative events.',
  confirmed: 'No confirmed events.',
  completed: 'No completed events yet.',
  cancelled: 'No cancelled events.',
  past:      'No past events yet.',
};

// ─── Styles (Enquiries / Reports conventions) ────────────────────────────────
const card = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '18px 22px', marginBottom: 18, boxShadow: 'var(--shadow-card)' };
const thStyle = { padding: '8px 12px', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--color-sunken)', borderBottom: '1px solid var(--color-border-strong)' };
const thNum = { ...thStyle, textAlign: 'right' };
const tdStyle = { padding: '9px 12px', fontSize: 'var(--text-sm)', color: 'var(--color-ink-brand-softer)', borderBottom: '1px solid var(--color-border-subtle)', verticalAlign: 'middle' };
const tdNum = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const tableWrap = { overflowX: 'auto', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' };
const tblStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' };
const sub = { fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)', marginTop: 2 };
const filterBtnStyle = (active) => ({
  padding: '7px 14px',
  minHeight: 36,
  background: active ? NAVY : 'var(--color-sunken)',
  color: active ? '#ffffff' : 'var(--color-ink-muted)',
  border: `1.5px solid ${active ? NAVY : 'var(--color-border-strong)'}`,
  borderRadius: 8,
  fontSize: '0.8rem',
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background-color 0.15s ease-out, color 0.15s ease-out, border-color 0.15s ease-out',
});

export default function Events({ onNavigate }) {
  const [rows, setRows] = useState(null);     // null = loading
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('upcoming');
  const [view, setView] = useState(() => { try { return sessionStorage.getItem('ev-view') === 'month' ? 'month' : 'list'; } catch (e) { return 'list'; } });
  const wide = useWide();
  const showMonth = wide && view === 'month';
  const today = istTodayIso();
  const pickView = (v) => { setView(v); try { sessionStorage.setItem('ev-view', v); } catch (e) { /* ignore */ } };

  const fetchEvents = useCallback(async () => {
    // Every column: the row is handed to EventForm as editData, and a partial
    // row there would save hidden fields (menu, terms, notes, reason) back as null.
    const { data, error: err } = await supabase
      .from('events')
      .select('*, event_line_items(unit_amount, quantity, tax_percent)')
      .order('event_date', { ascending: true })
      .limit(2000);
    if (err) { setError(`Could not load events: ${err.message}`); setRows([]); return; }
    setError('');
    setRows(data || []);
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Conflict flags (Stage 6): display only, loaded after the list has painted
  // so they never hold it up. Computed for every live upcoming event once per
  // load, whichever filter is showing.
  const [flags, setFlags] = useState(new Map());
  useEffect(() => {
    if (!rows) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      checkEventConflicts(rows, { today }).then(m => { if (!cancelled) setFlags(m); }).catch(() => {});
    }, 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [rows, today]);

  const visible = rows ? applyFilter(rows, filter, today) : [];
  const gridRows = rows && showMonth ? applyGridFilter(rows, filter, today) : [];
  const upcomingCount = rows ? applyFilter(rows, 'upcoming', today).length : 0;
  const openForm = (row) => { if (!row) return onNavigate('event-form', undefined, null); const { event_line_items, ...ev } = row; return onNavigate('event-form', undefined, ev); };

  // Second line under the title: type, then where on board.
  const whereText = (ev) => {
    const parts = [typeLabel(ev.event_type)];
    if (ev.cruise_types?.length) parts.push(ev.cruise_types.join(', '));
    if (ev.decks?.length) parts.push(ev.decks.join(', '));
    return parts.join(' · ');
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>Events</h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', margin: 0 }}>
            Weddings, corporate bookings, special nights, charters and dry dock.
            {rows ? ` ${upcomingCount} upcoming.` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="ev-view-toggle" role="group" aria-label="View">
            <button type="button" className={view === 'list' ? 'active' : ''} aria-pressed={view === 'list'} onClick={() => pickView('list')}>
              <List size={15} strokeWidth={2} aria-hidden="true" />List
            </button>
            <button type="button" className={view === 'month' ? 'active' : ''} aria-pressed={view === 'month'} onClick={() => pickView('month')}>
              <CalendarDays size={15} strokeWidth={2} aria-hidden="true" />Month
            </button>
          </div>
          <Button variant="primary" onClick={() => openForm(null)}>
            <Plus size={16} strokeWidth={2} aria-hidden="true" />New event
          </Button>
        </div>
      </div>

      <div style={card}>
        <div className="ev-filters" role="tablist" aria-label="Filter events">
          {FILTERS.map(f => (
            <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} style={filterBtnStyle(filter === f.key)} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>

        {error && <div className="toast toast-error" role="alert">{error}</div>}

        {rows === null ? (
          <div aria-busy="true" aria-label="Loading events">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="ev-skel-row">
                <span className="ev-skel" style={{ width: 84 }} />
                <span className="ev-skel" style={{ flex: 1 }} />
                <span className="ev-skel" style={{ width: 72 }} />
              </div>
            ))}
          </div>
        ) : showMonth ? (
          <EventMonth rows={gridRows} today={today} onOpen={openForm} filterLabel={filterLabelOf(filter)} />
        ) : visible.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--color-ink-muted)', fontSize: '0.9rem' }}>
            {EMPTY_COPY[filter]}
          </div>
        ) : (
          <>
            {/* Desktop: table */}
            <div className="ev-desktop" style={tableWrap}>
              <table className="data-table" style={tblStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Event</th>
                    <th style={thStyle}>Status</th>
                    <th style={thNum}>Pax</th>
                    <th style={thNum}>Agreed</th>
                    <th style={{ ...thStyle, width: 1 }} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map(ev => {
                    const st = statusOf(ev.status);
                    const dim = ev.status === 'cancelled';
                    return (
                      <tr key={ev.id} className="ev-row" onClick={() => openForm(ev)} style={{ cursor: 'pointer', opacity: dim ? 0.7 : 1 }}>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          <div style={{ fontWeight: 600, color: NAVY }}>{toDisplayDate(ev.event_date)} <span style={{ fontWeight: 500, color: 'var(--color-ink-muted)' }}>{weekdayOf(ev.event_date)}</span></div>
                          {ev.end_date && ev.end_date !== ev.event_date && <div style={sub}>to {toDisplayDate(ev.end_date)}</div>}
                          {timeText(ev) && <div style={sub}>{timeText(ev)}</div>}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600, color: NAVY }}>{ev.title}</div>
                          <div style={sub}>{whereText(ev)}</div>
                          <EventFlags lines={flags.get(ev.id)} />
                        </td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}><Badge tone={st.tone}>{st.label}</Badge></td>
                        <td style={tdNum}>{paxText(ev.pax_min, ev.pax_max) || <span style={{ color: 'var(--color-ink-faint)' }}>–</span>}</td>
                        <td style={{ ...tdNum, fontWeight: 600, color: NAVY }}>{fmtMoney(agreedOf(ev)) || <span style={{ color: 'var(--color-ink-faint)', fontWeight: 500 }}>–</span>}</td>
                        <td style={{ ...tdStyle, padding: '4px 8px' }}>
                          <Button variant="ghost" size="sm" aria-label={`Edit ${ev.title}`} onClick={(e) => { e.stopPropagation(); openForm(ev); }}>
                            <Pencil size={14} strokeWidth={1.75} aria-hidden="true" />Edit
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Phone: tappable cards */}
            <div className="ev-phone">
              {visible.map(ev => {
                const st = statusOf(ev.status);
                const meta = [paxText(ev.pax_min, ev.pax_max) && `${paxText(ev.pax_min, ev.pax_max)} pax`, fmtMoney(agreedOf(ev)), timeText(ev)].filter(Boolean).join(' · ');
                return (
                  <button key={ev.id} type="button" className="ev-card" onClick={() => openForm(ev)} style={{ opacity: ev.status === 'cancelled' ? 0.75 : 1 }}>
                    <div className="ev-card-top">
                      <span style={{ fontWeight: 700, color: NAVY, fontVariantNumeric: 'tabular-nums', fontSize: 'var(--text-sm)' }}>
                        {toDisplayDate(ev.event_date)}
                        <span style={{ fontWeight: 500, color: 'var(--color-ink-muted)' }}>
                          {ev.end_date && ev.end_date !== ev.event_date ? ` to ${toDisplayDate(ev.end_date)}` : ` ${weekdayOf(ev.event_date)}`}
                        </span>
                      </span>
                      <Badge tone={st.tone}>{st.label}</Badge>
                    </div>
                    <div style={{ fontWeight: 600, color: NAVY, fontSize: 'var(--text-md)', lineHeight: 1.3, marginTop: 4, overflowWrap: 'anywhere' }}>{ev.title}</div>
                    <div style={sub}>{whereText(ev)}</div>
                    {meta && <div style={{ ...sub, color: 'var(--color-ink-secondary)', fontVariantNumeric: 'tabular-nums' }}>{meta}</div>}
                    <EventFlags lines={flags.get(ev.id)} />
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
